/* src/components/Post.js - v4.27 - Debounced Video Autoplay
   CHANGELOG (v4.26 -> v4.27): Per explicit request, autoplay is no
   longer instant on crossing 50% visible - threshold raised to 0.9
   ("fully in focus") and playback waits 400ms after that, cancelled if
   the video scrolls back out before the timer fires. Scrolling quickly
   past a run of video posts no longer sets them all playing for a
   split second each; only a video someone actually stops on starts.
   See the effect's own comment (v4.25/v4.27) for the full reasoning.
   CHANGELOG (v4.25 -> v4.26): Per explicit request:
   - Videos now autoplay (muted, looping) as they scroll into view via a
     new IntersectionObserver effect (videoRef + threshold 0.5) - plays
     when 50%+ visible, pauses when scrolled away. Gated on the new
     user.video_autoplay pref (Profile.js v6.34, default true) so
     disabling it just leaves a normal poster-frame + manual-controls
     player. Deliberately NOT a blanket <video autoPlay> - with
     pagination, many video posts can be mounted off-screen at once, and
     autoplaying all of them would undo the exact bandwidth-consciousness
     this feature's whole backend (video_processing.py) was built around.
   - muted now reads user.video_muted_default (default true) instead of
     being unconditionally true.
   - Video sizing changed from width-relative (maxHeight:'100cqw', same
     pattern as the image block) to a fixed maxHeight:'600px' +
     objectFit:'contain' - this is the actual Facebook-style letterboxing
     (bounded card height regardless of the source video's aspect ratio,
     pillarboxed against the black background rather than either cropping
     a vertical video or letting a tall one blow out the feed's height).
   CHANGELOG (v4.24 -> v4.25): Posts can now carry a video (post.video,
   transcoded server-side - see routes/posts.py's /api/upload/video and
   video_processing.py) alongside the existing image field. Rendered as a
   real <video controls poster={post.video_thumbnail}>, same wrapper/
   sizing as the image block right below it, not folded into the image
   branch since a post could in principle carry both fields and they need
   different tags either way.
   CHANGELOG (v4.23 -> v4.24): Per explicit follow-up:
   - Main post image wrapper now gets marginLeft/marginRight:'auto' so it
     centers within the card once the card is wider than the 800px cap
     (previously it stayed left-aligned since a block element with
     maxWidth doesn't auto-center without explicit auto margins).
   - Lightbox popup had a visible translucent "halo" wider/taller than
     the actual visible picture. Cause: the outer box had a fixed pixel
     width and the <img> was width:'100%' + maxHeight:'90vh' with
     objectFit:'contain' - when a tall image hit the height cap, contain
     letterboxed the actual pixels narrower inside the img's own box,
     but the img's box (and its borderRadius/boxShadow) stayed at the
     full fixed width, leaving visible empty space + shadow on both
     sides. Fixed by moving all sizing (maxWidth: min(lightboxWidth,
     95vw), maxHeight: 90vh, width/height: 'auto') directly onto the
     <img> itself and dropping the outer box's own width/maxWidth/
     maxHeight entirely - a position:fixed div with no explicit width
     shrink-wraps to its content by default, so the box (and therefore
     the shadow/border-radius/close-button-anchor) now exactly hugs
     whatever size the image actually renders at, on every axis.
   CHANGELOG (v4.22 -> v4.23): Per explicit follow-up (with screenshot): a
   tall popup image was visibly cut off, only its lower portion reachable.
   Root cause: `.card` in App.css has `backdrop-filter: blur(15px)`, and
   per spec, filter/backdrop-filter establish a new containing block for
   position:fixed descendants - so the lightbox (a DOM descendant of
   .card) was never actually fixed to the viewport at all. It was
   silently repositioned relative to .card, which put it right back
   under the ancestor `overflow:hidden` wrapper Feed.js puts around every
   post card (for the rounded-corner glow effect), clipping anything
   taller than the card. Fixed by rendering the lightbox through
   createPortal(..., document.body) - it's now a real child of <body> in
   the DOM, completely outside .card's filtered/clipped subtree, so
   position:fixed works as originally intended and it can never be
   clipped by any post-card ancestor again. Also added maxHeight:'90vh'
   with objectFit:'contain' on the image itself (still respecting the
   existing 600px/800px width caps) - without this, a fixed box anchored
   to bottom:5vh with an unconstrained height could still extend above
   the top of the viewport with no way to scroll up and reach it, since
   fixed elements don't scroll with the page. This is a viewport-relative
   safety bound (always fits on screen), not the arbitrary pixel-height
   cap that was explicitly removed last round.
   Also brought back click-the-image-to-close (removed in v4.19, replaced
   with only the ✕ button + click-outside) alongside the existing
   click-outside listener - per explicit request, since a click-outside
   escape hatch may not exist on very small/mobile screens where the
   lightbox fills most of the viewport.
   CHANGELOG (v4.21 -> v4.22): Two bugs found while investigating a reported
   page-wide "laggy" feel that started after v4.21 shipped:
   - Root cause of the lag: v4.21's main-post-image cap was measured in JS
     (postImageWrapRef + getBoundingClientRect() inside a useEffect that ran
     AFTER first paint). Every post with an image rendered at full
     uncapped height for one frame, then snapped down once the effect
     fired - a visible flash-then-snap layout shift, and each measurement
     forced a synchronous layout reflow. With many posts in the feed this
     adds up. Replaced entirely with a pure-CSS fix: the wrapping div gets
     containerType:'inline-size' and the image gets maxHeight:'100cqw'
     (container query units = 100% of the container's own inline size,
     i.e. its width) - same "cap tall images to a square-ish preview,
     leave wide images alone" behavior, but resolved by the browser's
     layout engine directly with no JS measurement, no extra effect, and
     no post-mount reflow/flash. postImageWrapRef/postImageMaxHeight state
     removed as dead code now that nothing measures anything.
   - Root cause of the lightbox popup looking wrong (reported with a
     screenshot): App.css has a global `.card img:not(.avatar) { ...
     object-fit: contain; max-height: 800px; }` rule. The lightbox <img>
     lives inside a .card in the DOM (even though position:fixed pulls it
     out of the visual flow) and never overrided max-height/object-fit
     inline, so it silently inherited that 800px cap - explicitly
     unwanted for popups per feedback. Fixed with maxHeight:'none' and
     objectFit:'unset' inline on the shared lightbox image (used by both
     comment and post popups) to fully defeat the global rule.
   - openLightbox(url) now takes a second maxCap argument (default 600,
     used by comment images unchanged). Main post images now pass 800,
     so the post popup can render up to 800px wide while comment popups
     stay capped at 600px, per feedback. Main post inline image also
     capped at maxWidth:800px (via the same wrapping div).
   CHANGELOG (v4.20 -> v4.21): Per explicit follow-up feedback:
   - Lightbox width cap raised from 400px to 600px (still measured against
     the post card's own rendered width via cardRef, whichever is smaller -
     unchanged on narrow/mobile browsers).
   - The MAIN post's own image (post.image, separate from comment images)
     now gets the same "capped preview, click for full view" treatment
     comment images already had. A new postImageWrapRef measures the
     wrapping div's rendered width on mount/post.image change (via a new
     useEffect) and that pixel value is applied as the image's maxHeight
     with objectFit:'cover'. A wide/landscape image whose natural height
     never reaches that cap displays unaffected at full natural height;
     a tall/portrait image (e.g. 600w x 1000h) gets cropped to a roughly
     square preview (~600x600) instead of stretching the post card
     downward. Clicking the image opens the same lightbox already built
     for comment images (openLightbox(post.image)), so the full image is
     still reachable.
   CHANGELOG (v4.19 -> v4.20): v4.19 still sized the lightbox against the
   VIEWPORT (85vw/80vh) with a dark full-screen backdrop - per explicit
   follow-up feedback (still not right), rebuilt properly:
   - Width is now capped at min(the post card's own current rendered
     width, 400px) - measured live via a new cardRef + getBoundingClientRect()
     on open (openLightbox()), not a percentage of the browser window. On a
     narrow/mobile browser the card itself is already narrower than 400,
     so this naturally stays within the browser width with no extra media
     query needed. Height is unset (auto) so the image keeps its own
     aspect ratio at that width - never stretched.
   - The full-screen dark backdrop is gone entirely - no background at
     all. Closing now happens via the ✕ button or a click-outside listener
     (added to the same handleClickOutside effect already used for the
     post's own ⋮ menu and the comment GIF/emoji menu), not a backdrop
     click.
   - Still position:'fixed' (not absolute-within-the-card) specifically so
     it can never be clipped by anything - the earlier "looks cut off on
     the sides" complaint - while still being capped to the post's actual
     width via the measured pixel value rather than inheriting the card's
     own box (which could clip it if any ancestor has overflow:hidden).
     bottom:'5vh' keeps the previous bottom-anchoring behavior.
   CHANGELOG (v4.18 -> v4.19): Per explicit request (with a screenshot):
   the lightbox's dark backdrop filling the entire viewport with the image
   centered read as a "huge black window", causing unnecessary scrolling
   on some layouts. The image itself was never actually stretched (it
   already used objectFit:contain), but centering it in a full-height
   flex container visually dominated the screen regardless of the
   image's real size. Now: alignItems:'flex-end' + paddingBottom:'5vh'
   anchors the image toward the bottom of the viewport instead of dead
   center, and the image gets explicit width:'auto'/height:'auto' (renders
   at its own natural pixel size) with maxWidth:'85vw'/maxHeight:'80vh'
   only as safety caps for oversized images, not a percentage the image is
   forced to stretch toward. Backdrop opacity also nudged down slightly
   (0.9 -> 0.75) so it reads less like a solid black wall. Close button
   switched from position:'absolute' (relative to the now bottom-aligned
   flex container, which would put it near the image) to position:'fixed'
   so it stays pinned to the actual top-right corner of the screen.
   CHANGELOG (v4.17 -> v4.18): Comment attachment thumbnails are
   deliberately small (max 220x220), but were unclickable - no way to see
   the actual image at full size. Added a simple lightbox: clicking the
   thumbnail opens it full-screen (objectFit: contain, so nothing gets
   cropped/stretched), click anywhere (or the ✕) to close. New
   lightboxImage state, local to each Post instance.
   CHANGELOG (v4.16 -> v4.17):
   - Comment images/GIFs were rendering centered instead of left-aligned -
     root cause was App.css's global `.card img:not(.avatar) { margin: 15px
     auto; }` rule, which every image inside a .card (posts AND, it turns
     out, comments) inherits unless overridden. Added `margin: 0` inline on
     both the comment attachment image and the pre-submit preview image -
     inline styles win over the external stylesheet rule regardless of its
     specificity, so this was simpler and safer than editing the shared
     CSS rule itself (which posts still rely on for their own centering).
   - Added comment up/down voting: small ▲ count ▼ widget per comment,
     same visual language as the post's own vote arrows but compact.
     Calls the new onVoteComment(postId, commentId, vote) prop (wired
     through App.js v13.36 -> routes/posts.py v2.9's new vote endpoint),
     following the same fire-and-refetch pattern already used by comment
     edit/delete (post.comments flows back down as props after every
     action) rather than the optimistic local-state pattern the post's
     OWN like button uses - kept consistent with how comments already
     behave. Guests can click (matches handleVote's own pattern) and get
     the login prompt via onTriggerLogin instead of the vote silently
     failing.
   CHANGELOG (v4.15 -> v4.16): First item off the new backlog (comments
   first, web first, per explicit sequencing decision). Comments can now
   carry an image/file the same way posts already can: Photo (uploads via
   the existing /api/upload, same endpoint posts use), GIF (Giphy search,
   using the giphyKey now passed down from Feed.js), and Emoji (same
   COMMON_EMOJIS list as the post composer, appended into the comment
   text). New state mirrors Feed.js's own composer pattern closely
   (commentImage/uploadingCommentImage/commentMenu/commentGifs/etc.) but
   scoped per-Post instance rather than per-page, since each post's
   comment box is independent. onAddComment now receives a third `image`
   arg (App.js v13.35 forwards it to routes/posts.py v2.8's new comment
   field). Comments with an attachment render it below the text (PDF
   attachments get the same icon+link treatment as post PDFs; anything
   else renders as an image) - text is now optional on a comment as long
   as an image is attached, matching the backend's new validation.
   CHANGELOG (v4.14 -> v4.15): Per explicit request, the sentiment
   indicator (Feed.js's top banner, removed in this same change) is now a
   right-side accent border instead - mirrors the existing left border
   (post.color, the user's chosen accent), same 6px width. Green for
   POSITIVE, red for NEGATIVE, no border (same as before) for neutral/no
   sentiment. Reason given: the top banner's color contrasted too harshly
   against the card itself; a border in the same visual language as the
   post's own existing left accent reads as part of the card's design
   rather than a separate loud element sitting on top of it.
   CHANGELOG (v4.13 -> v4.14):
   - Posts not made today now show both the date and time (e.g.
     "1/15/2026 3:45 PM") instead of just the date, for more precision.
     Today's posts still show time only.
   CHANGELOG (v4.12 -> v4.13):
   - post.timestamp was rendered raw with no formatting, and was missing
     entirely on ~98% of existing posts (confirmed: 1,235 of 1,264 posts in
     posts.json have no timestamp field at all). Even when present, it only
     ever stored a bare time string with no date, generated fresh at post
     creation, so old posts could never show a correct date. Replaced with
     formatPostTimestamp(post.id) - post.id is always a reliable
     epoch-millisecond value on every post, so this now correctly shows just
     the time for posts made today, and the date for anything older. */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';

// Same lists as Feed.js's own composer (COMMON_EMOJIS/FALLBACK_GIFS) -
// duplicated locally rather than imported, since they're just small
// static constants and Feed.js doesn't export them.
const COMMENT_EMOJIS = [
    '😀','😂','😍','😎','🤔','😭','😡','👍','👎','🔥',
    '❤️','💔','✨','🎉','🚀','💀','🤡','💩','👻','👽',
    '🤖','🐱','🐶','🍕','🍺','☕','💻','📱','🔋','💡'
];
const COMMENT_FALLBACK_GIFS = [
    { id: 'f1', images: { fixed_height: { url: "https://media.giphy.com/media/l0HlHJGHe3yAMhdQY/giphy.gif" }, original: { url: "https://media.giphy.com/media/l0HlHJGHe3yAMhdQY/giphy.gif" } } },
    { id: 'f2', images: { fixed_height: { url: "https://media.giphy.com/media/3o7TKr3nzbh5WgCFxe/giphy.gif" }, original: { url: "https://media.giphy.com/media/3o7TKr3nzbh5WgCFxe/giphy.gif" } } },
    { id: 'f3', images: { fixed_height: { url: "https://media.giphy.com/media/xT9IgG50Fb7Mi0prBC/giphy.gif" }, original: { url: "https://media.giphy.com/media/xT9IgG50Fb7Mi0prBC/giphy.gif" } } }
];

// post.id is always a reliable epoch-millisecond value set at creation time
// on every post (backend and frontend both generate it this way). The
// separate post.timestamp field is not reliable - most posts never had it
// set at all, and when it was set, it only ever stored a bare time string
// with no date, making it useless for anything not created today. This
// derives the displayed date/time from post.id instead: today's posts show
// just the time, older posts show the date.
const formatPostTimestamp = (id) => {
  if (!id) return '';
  const date = new Date(id);
  if (isNaN(date.getTime())) return '';
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  return isToday
    ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

function Post({ post, user, onDelete, onLike, onAddComment, onVoteComment, onDeleteComment, onEditComment, onEdit, onNavigateProfile, onShare, onTriggerLogin, giphyKey }) {
  const [showComments, setShowComments] = useState(false);
  const [commentInput, setCommentInput] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(post.message);
  const [editVisibility, setEditVisibility] = useState(post.visibility || 'public');

  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editCommentText, setEditCommentText] = useState('');
  const [showShareMenu, setShowShareMenu] = useState(false);

  // --- COMMENT ATTACHMENTS (photo/file/GIF/emoji) ---
  const [commentImage, setCommentImage] = useState(null);
  const [uploadingCommentImage, setUploadingCommentImage] = useState(false);
  const [commentMenu, setCommentMenu] = useState(null); // null | 'gif' | 'emoji'
  const [commentGifs, setCommentGifs] = useState([]);
  const [commentGifSearch, setCommentGifSearch] = useState('');
  const [commentGifLoading, setCommentGifLoading] = useState(false);
  const commentFileInputRef = useRef(null);
  const commentMenuRef = useRef(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxWidth, setLightboxWidth] = useState(600);
  const cardRef = useRef(null);
  const videoRef = useRef(null);
  const lightboxRef = useRef(null);

  // NEW: Post Menu State
  const [showMenu, setShowMenu] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('');
  
  const menuRef = useRef(null);

  // GUEST GUARD: Only check voters if user exists
  const initialUserVote = (user && post.voters && post.voters[user.name]) ? post.voters[user.name] : 0;
  const [votes, setVotes] = useState(post.likes || 0);
  const [userVote, setUserVote] = useState(initialUserVote); 

  // --- ADMIN & OWNER LOGIC ---
  const isMyPost = user && user.name === post.username;
  const isAdmin = user && (user.isAdmin === true || user.role === 'admin' || user.username === 'admin'); 
  const canDelete = isMyPost || isAdmin;
  
  // --- CLICK OUTSIDE LISTENER ---
  useEffect(() => {
    function handleClickOutside(event) {
        if (menuRef.current && !menuRef.current.contains(event.target)) {
            setShowMenu(false);
        }
        if (commentMenuRef.current && !commentMenuRef.current.contains(event.target)) {
            setCommentMenu(null);
        }
        if (lightboxRef.current && !lightboxRef.current.contains(event.target)) {
            setLightboxImage(null);
        }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
      if (isEditing) {
          setEditText(post.message);
          setEditVisibility(post.visibility || 'public');
      }
  }, [isEditing, post]);

  // --- LINK FORMATTER ---
  const formatMessage = (text) => {
      if (!text) return null;
      const urlRegex = /(https?:\/\/[^\s]+)/g;
      const parts = text.split(urlRegex);
      return parts.map((part, index) => {
          if (part.match(urlRegex)) {
              const fullUrl = part;
              const displayText = fullUrl.length > 50 ? fullUrl.substring(0, 47) + '...' : fullUrl;
              return (
                  <a 
                      key={index} href={fullUrl} target="_blank" rel="noopener noreferrer" 
                      onClick={(e) => e.stopPropagation()} 
                      style={{ color: '#1e90ff', textDecoration: 'none', wordBreak: 'break-all' }}
                      onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                      onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                  >
                      {displayText}
                  </a>
              );
          }
          return part;
      });
  };

  const handleVote = (type) => {
    if (!user) { if(onTriggerLogin) onTriggerLogin(); return; } 
    let newVote = type;
    if (userVote === type) newVote = 0; 
    const diff = newVote - userVote;
    setVotes(votes + diff);
    setUserVote(newVote);
    onLike(post.id, type);
  };

  const handleVoteComment = (commentId, vote) => {
    if (!user) { if (onTriggerLogin) onTriggerLogin(); return; }
    if (onVoteComment) onVoteComment(post.id, commentId, vote);
  };

  // Caps the lightbox at the post card's own current rendered width (never
  // wider than the post, and on a narrow/mobile browser the card itself is
  // already narrower than 400, so this naturally shrinks with it) - not a
  // fixed viewport percentage, which would size against the whole browser
  // window instead of the post.
  const openLightbox = (url, maxCap = 600) => {
      const cardWidth = cardRef.current ? cardRef.current.getBoundingClientRect().width : maxCap;
      setLightboxWidth(Math.min(cardWidth, maxCap));
      setLightboxImage(url);
  };

  const submitComment = () => {
    if (!user) { if(onTriggerLogin) onTriggerLogin(); return; }
    if (commentInput.trim() !== '' || commentImage) {
      onAddComment(post.id, commentInput, commentImage);
      setCommentInput('');
      setCommentImage(null);
    }
  };

  const triggerCommentFileUpload = () => {
      if (commentFileInputRef.current) commentFileInputRef.current.click();
  };

  const handleCommentFileSelect = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      e.target.value = null;
      const formData = new FormData();
      formData.append('file', file);
      formData.append('username', user.name);
      setUploadingCommentImage(true);
      try {
          const res = await fetch('/api/upload', { method: 'POST', body: formData });
          const data = await res.json();
          if (res.ok) setCommentImage(data.url);
          else alert("Upload failed. Please try a smaller file.");
      } catch (err) { console.error("Comment upload error:", err); } finally { setUploadingCommentImage(false); }
  };

  const searchCommentGifs = useCallback(async (term) => {
      setCommentGifLoading(true);
      try {
          if (!giphyKey) {
              setCommentGifs(COMMENT_FALLBACK_GIFS);
              setCommentGifLoading(false);
              return;
          }
          const query = term || "trending";
          const res = await fetch(`https://api.giphy.com/v1/gifs/search?api_key=${giphyKey}&q=${query}&limit=20&rating=g`);
          const data = await res.json();
          if (data.meta && data.meta.status === 200 && data.data.length > 0) setCommentGifs(data.data);
          else setCommentGifs(COMMENT_FALLBACK_GIFS);
      } catch (e) { setCommentGifs(COMMENT_FALLBACK_GIFS); }
      setCommentGifLoading(false);
  }, [giphyKey]);

  useEffect(() => {
      if (commentMenu === 'gif') searchCommentGifs('trending');
  }, [commentMenu, searchCommentGifs]);

  // --- VIDEO AUTOPLAY-ON-SCROLL (v4.25, debounced in v4.27) ---
  // Per-post video, so this can't just be a blanket <video autoPlay> -
  // with pagination/infinite scroll, many video posts can be mounted in
  // the DOM at once (most of them off-screen), and autoplaying all of
  // them simultaneously would be exactly the kind of bandwidth waste
  // this project has been actively trying to avoid elsewhere (see
  // video_processing.py's whole design). Only the video actually
  // scrolled into view plays; leaving the viewport pauses it. Gated on
  // user.video_autoplay (default true, Profile.js's new toggle) -
  // disabled entirely just leaves the poster frame + manual controls.
  //
  // v4.27: threshold raised to 0.9 ("fully in focus", not just half
  // visible) and playback is debounced 400ms after crossing that
  // threshold, rather than starting immediately - per explicit request,
  // scrolling quickly past several video posts in a row (each briefly
  // crossing the visibility threshold) shouldn't set them all playing.
  // The timeout is cancelled if the video leaves view again before it
  // fires, so only a video someone actually stops on for a moment
  // starts playing - a fast scroll-by never triggers playback at all.
  useEffect(() => {
      if (!post.video || !videoRef.current) return;
      if (user && user.video_autoplay === false) return;
      const el = videoRef.current;
      let playTimeout = null;
      const observer = new IntersectionObserver(
          (entries) => {
              entries.forEach((entry) => {
                  if (entry.isIntersecting) {
                      playTimeout = setTimeout(() => {
                          el.play().catch(() => {}); // browser autoplay policy can reject this - fine, controls still work manually
                      }, 400);
                  } else {
                      if (playTimeout) { clearTimeout(playTimeout); playTimeout = null; }
                      el.pause();
                  }
              });
          },
          { threshold: 0.9 }
      );
      observer.observe(el);
      return () => {
          if (playTimeout) clearTimeout(playTimeout);
          observer.disconnect();
      };
  }, [post.video, user]);

  const handleCommentGifSelect = (gifUrl) => {
      setCommentImage(gifUrl);
      setCommentMenu(null);
  };

  const addCommentEmoji = (emoji) => {
      setCommentInput(prev => prev + emoji);
  };

  // --- REPORT & MUTE HANDLERS ---
  const handleMute = async () => {
      if (!user) return onTriggerLogin();
      if (!window.confirm(`Are you sure you want to mute @${post.username}? Their posts will no longer appear in your feed.`)) return;
      
      try {
          const res = await fetch('/api/users/mute', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ current_user: user.name, target_user: post.username })
          });
          const data = await res.json();
          alert(data.message);
          window.location.reload(); // Refresh to hide posts
      } catch(e) { console.error(e); }
  };


  // --- UPDATED REPORT HANDLER ---
  const handleReportSubmit = async () => {
      if (!reportReason) return alert("Please enter a reason.");
      try {
          await fetch('/api/report', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ 
                  reporter: user.name, 
                  target_id: `Post ${post.id} by ${post.username}`, 
                  reason: reportReason,
                  link: `/post/${post.id}` // NEW: Direct Link
              })
          });
          
          setShowReportModal(false);
          setReportReason('');
          
          if (window.confirm("Report submitted. Would you also like to mute this user?")) {
              await handleMute();
          } else {
              alert("Report sent to admins.");
          }
      } catch (e) { console.error(e); }
  };

  const startEditComment = (comment) => {
      setEditingCommentId(comment.id);
      setEditCommentText(comment.text);
  };

  const saveEditComment = (commentId) => {
      onEditComment(post.id, commentId, editCommentText);
      setEditingCommentId(null);
  };

  const handleEditCommentKeyDown = (e, commentId) => {
      if (e.key === 'Enter') saveEditComment(commentId);
  };

  const handleSavePost = () => {
      if (onEdit) {
          onEdit(post.id, editText, editVisibility);
          setIsEditing(false);
      }
  };

  const handleEditKeyDown = (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault(); 
          handleSavePost();
      }
  };

  const handleShareAction = (type) => {
      if (type === 'repost' && !user) { if(onTriggerLogin) onTriggerLogin(); return; }
      onShare(post, type);
      setShowShareMenu(false);
  };

  const isPdf = post.fileType === 'pdf' || (post.image && post.image.toLowerCase().endsWith('.pdf'));

  const getVisibilityIcon = (vis) => {
      if (vis === 'friends') return <span title="Friends Only">👥</span>;
      if (vis === 'private') return <span title="Private">🔒</span>;
      return <span title="Public">🌍</span>;
  };

  const getSentimentBorderColor = (sentiment) => {
      if (sentiment === 'POSITIVE') return '#2ed573';
      if (sentiment === 'NEGATIVE') return '#ff4757';
      return null;
  };
  const sentimentBorderColor = getSentimentBorderColor(post.sentiment);

  return (
    <div ref={cardRef} className="card" style={{
        position: 'relative',
        borderLeft: `6px solid ${post.color || '#B0BEC5'}`,
        borderRight: sentimentBorderColor ? `6px solid ${sentimentBorderColor}` : 'none',
        textAlign: 'left'
    }}>
      
      
      {/* ACTIONS */}
      <div style={{ position: 'absolute', top: '15px', right: '15px', display: 'flex', gap: '10px', zIndex: 10 }}>
        {/* EDIT BUTTON: Only show for owner */}
        {isMyPost && !isEditing && (
            <button onClick={() => setIsEditing(true)} style={{ background: 'transparent', border: 'none', color: '#b0b3b8', cursor: 'pointer', fontSize: '16px' }} title="Edit Post">✏️</button>
        )}
        
        {/* DELETE BUTTON: Show for Owner OR Admin */}
        {canDelete && (
            <button onClick={() => onDelete(post.id)} style={{ background: 'transparent', border: 'none', color: '#b0b3b8', cursor: 'pointer', fontSize: '16px' }} title={isAdmin && !isMyPost ? "Admin Delete" : "Delete Post"}>🗑️</button>
        )}
        
        {/* 3DOT MENU */}
        {!isMyPost && user && (
            <div ref={menuRef} style={{ position: 'relative' }}>
                <button onClick={() => setShowMenu(!showMenu)} style={{ background: 'transparent', border: 'none', color: '#b0b3b8', cursor: 'pointer', fontSize: '18px', fontWeight: 'bold' }}>⋮</button>
                {showMenu && (
                    <div style={{ position: 'absolute', right: 0, top: '25px', background: '#2f3542', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '5px', width: '120px', overflow: 'hidden', boxShadow: '0 5px 15px rgba(0,0,0,0.5)', zIndex: 100 }}>
                        <button onClick={() => { setShowReportModal(true); setShowMenu(false); }} style={{ display: 'block', width: '100%', padding: '10px', textAlign: 'left', background: 'transparent', border: 'none', color: '#ff4757', cursor: 'pointer', fontSize:'0.9rem' }}>🚩 Report</button>
                        <button onClick={() => { handleMute(); setShowMenu(false); }} style={{ display: 'block', width: '100%', padding: '10px', textAlign: 'left', background: 'transparent', border: 'none', color: '#ccc', cursor: 'pointer', fontSize:'0.9rem' }}>🔇 Mute</button>
                    </div>
                )}
            </div>
        )}
      </div>

      {/* REPORT MODAL */}
      {showReportModal && (
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.8)', zIndex: 2000, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <div className="card" style={{ width: '90%', maxWidth: '400px', border: '1px solid #ff4757' }}>
                  <h3 style={{ marginTop: 0, color: '#ff4757' }}>Report Post</h3>
                  <p style={{ fontSize: '0.9rem', color: '#ccc' }}>Please provide a reason for reporting this post.</p>
                  <textarea 
                      value={reportReason} 
                      onChange={(e) => setReportReason(e.target.value)} 
                      placeholder="Spam, harassment, malicious link..." 
                      style={{ width: '100%', height: '80px', padding: '10px', borderRadius: '5px', border: 'none', background: 'rgba(255,255,255,0.1)', color: 'white', marginBottom: '15px' }}
                  />
                  <div style={{ display: 'flex', gap: '10px' }}>
                      <button onClick={() => setShowReportModal(false)} style={{ flex: 1, padding: '10px', background: 'transparent', border: '1px solid #aaa', color: '#aaa', borderRadius: '5px', cursor: 'pointer' }}>Cancel</button>
                      <button onClick={handleReportSubmit} style={{ flex: 1, padding: '10px', background: '#ff4757', border: 'none', color: 'white', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}>Submit Report</button>
                  </div>
              </div>
          </div>
      )}

      {/* HEADER & CONTENT (Same as before) */}
      <div className="card-header" style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'flex-start',
          flexDirection: 'row',
          marginBottom: '15px',
          textAlign: 'left' 
      }}>
        {post.userImage ? (
            <img src={post.userImage} style={{ width: '40px', height: '40px', borderRadius: '50%', marginRight: '15px', objectFit: 'cover' }} alt="Avatar" />
        ) : (
            <div style={{ background: post.color || '#444', width: '40px', height: '40px', borderRadius: '50%', marginRight: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', flexShrink: 0 }}>
                {post.username[0].toUpperCase()}
            </div>
        )}
        
        <div className="username" style={{ cursor: 'pointer', textAlign: 'left', flexGrow: 1 }} onClick={() => onNavigateProfile && onNavigateProfile(post.username)}>
            <div style={{ fontWeight: 'bold' }}>{post.username} {post.feeling && <span style={{ fontWeight: 'normal', fontSize: '0.9rem', color: '#888', marginLeft: '8px' }}>is feeling {post.feeling}</span>}</div>
            {post.location && <div style={{ fontSize: '0.8rem', opacity: 0.7 }}>📍 {post.location}</div>}
            
            <div style={{ fontSize: '0.75rem', opacity: 0.5, display: 'flex', alignItems: 'center', gap: '5px' }}>
                {formatPostTimestamp(post.id)} • {getVisibilityIcon(post.visibility)}
            </div>
        </div>
      </div>
      
      {/* --- CONTENT BLOCK --- */}
      {isEditing ? (
        <div style={{ marginBottom: '15px', position: 'relative', zIndex: 20 }}>
            <div style={{ position: 'relative', width: '100%' }}>
                <textarea
                    className="post-input"
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    onKeyDown={handleEditKeyDown} 
                    style={{ width: '100%', minHeight: '100px', resize: 'vertical', padding: '10px', borderRadius: '10px', fontFamily: 'inherit', fontSize: '1rem', background: 'rgba(0,0,0,0.5)', color: 'white' }}
                />
                <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                    <select value={editVisibility} onChange={(e) => setEditVisibility(e.target.value)} style={{ background: '#333', color: 'white', border: '1px solid rgba(255,255,255,0.2)', padding: '5px 10px', borderRadius: '15px', cursor: 'pointer', outline: 'none' }}>
                        <option value="public" style={{background: '#333', color: 'white'}}>🌍 Public</option>
                        <option value="friends" style={{background: '#333', color: 'white'}}>👥 Friends</option>
                        <option value="private" style={{background: '#333', color: 'white'}}>🔒 Only Me</option>
                    </select>
                    <button type="button" onClick={handleSavePost} style={{ background: '#2ed573', border: 'none', color: 'white', padding: '5px 15px', borderRadius: '15px', cursor: 'pointer', fontWeight: 'bold' }}>Save</button>
                    <button type="button" onClick={() => setIsEditing(false)} style={{ background: 'transparent', border: '1px solid #777', color: '#ccc', padding: '5px 15px', borderRadius: '15px', cursor: 'pointer' }}>Cancel</button>
                </div>
            </div>
        </div>
      ) : (
        <p style={{ margin: '15px 0', whiteSpace: 'pre-wrap', textAlign: 'left' }}>
            {formatMessage(post.message)}
        </p>
      )}

      {post.video && (
        <div style={{ marginTop: '15px', maxWidth: '800px', marginLeft: 'auto', marginRight: 'auto', display: 'flex', justifyContent: 'center' }}>
            {/* Fixed max-height (not width-relative like the image block below) plus
                objectFit:'contain' - this is the Facebook-style letterboxed treatment:
                a tall/vertical video gets pillarboxed against the black background
                instead of blowing up the feed card's height to match its own aspect
                ratio, and a wide video just fits normally. */}
            <video
                ref={videoRef}
                src={post.video}
                poster={post.video_thumbnail || undefined}
                controls
                loop
                muted={!user || user.video_muted_default !== false}
                playsInline
                style={{
                    display: 'block',
                    width: '100%',
                    maxHeight: '600px',
                    objectFit: 'contain',
                    borderRadius: '10px',
                    border: '1px solid #3a3b45',
                    background: '#000',
                }}
            />
        </div>
      )}

      {post.image && (
        <div style={{ marginTop: '15px', maxWidth: '800px', marginLeft: 'auto', marginRight: 'auto', containerType: 'inline-size' }}>
            {isPdf ? (
                <a href={post.image} target="_blank" rel="noopener noreferrer" style={{ padding: '20px', background: '#2c3e50', borderRadius: '10px', border: '1px solid #34495e', textDecoration: 'none', color: 'white', display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <span style={{ fontSize: '30px' }}>📄</span><div><div style={{ fontWeight: 'bold' }}>PDF Attachment</div><div style={{ fontSize: '12px', opacity: 0.7 }}>Click to view document</div></div>
                </a>
            ) : (
                <img
                    src={post.image}
                    alt="Post content"
                    onClick={() => openLightbox(post.image, 800)}
                    style={{
                        display: 'block',
                        width: '100%',
                        maxHeight: '100cqw',
                        objectFit: 'cover',
                        borderRadius: '10px',
                        border: '1px solid #3a3b45',
                        cursor: 'pointer',
                    }}
                />
            )}
        </div>
      )}
      
      {/* --- ACTION BUTTONS --- */}
      <div className="post-actions" style={{ position: 'relative', marginTop: '15px', justifyContent: 'flex-start', display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
        
        <div
          className="vote-group"
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '0px',
          }}>

          {/* UP ARROW */}
          <button
            className="vote-arrow up"
            onClick={() => handleVote(1)}
            style={{
              color: userVote === 1 ? '#2ed573' : '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              padding: '0 5px',
            }}>
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="currentColor">
              <path d="M12 4l-10 16h20z" />
            </svg>
          </button>

          <span
            style={{
              fontWeight: 'bold',
              fontSize: '14px',
              color:
                userVote === 1
                  ? '#2ed573'
                  : userVote === -1
                    ? '#ff4757'
                    : '#fff',
              minWidth: '20px',
              textAlign: 'center',
            }}>
            {votes}
          </span>

          {/* DOWN ARROW */}
          <button
            className="vote-arrow down"
            onClick={() => handleVote(-1)}
            style={{
              color: userVote === -1 ? '#ff4757' : '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              padding: '0 5px',
            }}>
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="currentColor">
              <path d="M12 20l10-16H2z" />
            </svg>
          </button>
        </div>        
        <button className="action-btn" onClick={() => setShowComments(!showComments)} style={{ marginLeft: '10px' }}>💬 {post.comments ? post.comments.length : 0} Comments</button>
        
        <div style={{ position: 'relative', marginLeft: '10px' }}>
            <button className="action-btn" onClick={() => setShowShareMenu(!showShareMenu)}>🚀 Share</button>
            {showShareMenu && (
                <div className="card" style={{ position: 'absolute', bottom: '110%', left: '0', width: '160px', padding: '10px', zIndex: 50, display: 'flex', flexDirection: 'column', gap: '5px', background: 'rgba(30, 30, 36, 0.98)', border: '1px solid var(--glass-border)' }}>
                    <button onClick={() => handleShareAction('repost')} style={{ background: 'transparent', border: 'none', color: 'white', textAlign: 'left', padding: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>🔁 Repost</button>
                    <button onClick={() => handleShareAction('link')} style={{ background: 'transparent', border: 'none', color: 'white', textAlign: 'left', padding: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>🔗 Copy Link</button>
                </div>
            )}
        </div>
      </div>
      
      {showComments && (
        <div className="comment-section" style={{ marginTop: '15px' }}>
          <div className="comments-list">
            {post.comments && post.comments.map((c) => {
              const isCommentPdf = c.image && c.image.toLowerCase().endsWith('.pdf');
              const commentUserVote = (user && c.voters) ? (c.voters[user.name] || 0) : 0;
              return (
              <div key={c.id} style={{ marginBottom: '8px', padding: '10px', background: 'rgba(0,0,0,0.2)', borderRadius: '8px', fontSize: '0.9rem', textAlign: 'left' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                   {editingCommentId === c.id ? (
                       <div style={{ width: '100%', display: 'flex', alignItems: 'center', position: 'relative' }}>
                           <input className="comment-input" value={editCommentText} onChange={(e) => setEditCommentText(e.target.value)} onKeyDown={(e) => handleEditCommentKeyDown(e, c.id)} style={{ paddingRight: '70px' }} autoFocus />
                           <div style={{ position: 'absolute', right: '10px', display: 'flex', gap: '5px' }}>
                               <button onClick={() => saveEditComment(c.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent-color)', fontSize: '18px' }}>➤</button>
                               <button onClick={() => setEditingCommentId(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ccc', fontSize: '14px' }}>✕</button>
                           </div>
                       </div>
                   ) : (
                       <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
                           <div style={{ display: 'flex', alignItems: 'center', gap: '2px', flexShrink: 0 }}>
                               <button onClick={() => handleVoteComment(c.id, 1)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: commentUserVote === 1 ? '#2ed573' : '#888', fontSize: '11px', padding: '2px', lineHeight: 1 }}>▲</button>
                               <span style={{ fontSize: '0.75rem', minWidth: '14px', textAlign: 'center', color: commentUserVote === 1 ? '#2ed573' : commentUserVote === -1 ? '#ff4757' : '#aaa' }}>{c.likes || 0}</span>
                               <button onClick={() => handleVoteComment(c.id, -1)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: commentUserVote === -1 ? '#ff4757' : '#888', fontSize: '11px', padding: '2px', lineHeight: 1 }}>▼</button>
                           </div>
                           <div>{c.text && <><span style={{fontWeight: 'bold', marginRight: '5px'}}>{c.user}:</span>{c.text}</>}{!c.text && <span style={{fontWeight: 'bold'}}>{c.user}</span>}</div>
                       </div>
                   )}

                   {/* ADMIN GUARD: Owner OR Admin can delete comments */}
                   {user && (c.user === user.name || isAdmin) && editingCommentId !== c.id && (
                       <div style={{ marginLeft: '10px', display: 'flex', gap: '5px' }}>
                           {c.user === user.name && (
                               <button onClick={() => startEditComment(c)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', opacity: 0.7 }}>✏️</button>
                           )}
                           <button onClick={() => onDeleteComment(post.id, c.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', opacity: 0.7 }}>🗑️</button>
                       </div>
                   )}
                </div>
                {c.image && editingCommentId !== c.id && (
                    <div style={{ marginTop: '8px' }}>
                        {isCommentPdf ? (
                            <a href={c.image} target="_blank" rel="noopener noreferrer" style={{ padding: '10px', background: '#2c3e50', borderRadius: '8px', border: '1px solid #34495e', textDecoration: 'none', color: 'white', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.85rem' }}>
                                <span style={{ fontSize: '20px' }}>📄</span><span>PDF Attachment</span>
                            </a>
                        ) : (
                            <img src={c.image} alt="Comment attachment" onClick={() => openLightbox(c.image)} style={{ maxWidth: '220px', maxHeight: '220px', borderRadius: '8px', border: '1px solid #3a3b45', margin: 0, cursor: 'pointer' }} />
                        )}
                    </div>
                )}
              </div>
              );
            })}
          </div>

          {commentImage && (
              <div style={{ position: 'relative', display: 'inline-block', marginTop: '10px' }}>
                  <img src={commentImage} alt="Attachment preview" style={{ maxHeight: '120px', borderRadius: '8px', border: '1px solid #3a3b45', margin: 0 }} />
                  <button onClick={() => setCommentImage(null)} style={{ position: 'absolute', top: '4px', right: '4px', background: 'rgba(0,0,0,0.7)', color: 'white', border: 'none', borderRadius: '50%', width: '22px', height: '22px', cursor: 'pointer', fontSize: '12px' }}>✕</button>
              </div>
          )}

          <div style={{ position: 'relative', width: '100%', marginTop: '10px' }}>
            <input type="text" className="comment-input" placeholder="Write a comment..." value={commentInput} onChange={(e) => setCommentInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && submitComment()} style={{ paddingRight: '35px' }} />
            <button onClick={submitComment} disabled={uploadingCommentImage} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', color: '#b0b3b8', fontSize: '16px', cursor: 'pointer' }}>➤</button>
          </div>

          <div ref={commentMenuRef} style={{ position: 'relative', display: 'flex', gap: '10px', marginTop: '8px', fontSize: '0.8rem' }}>
              <input type="file" ref={commentFileInputRef} style={{ display: 'none' }} accept="image/*,application/pdf" onChange={handleCommentFileSelect} />
              <span onClick={triggerCommentFileUpload} style={{ color: '#2ed573', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', padding: '5px 8px', background: 'rgba(46, 213, 115, 0.1)', borderRadius: '10px' }}>
                  {uploadingCommentImage ? '⏳' : '📁'} Photo
              </span>
              <span onClick={() => setCommentMenu(commentMenu === 'gif' ? null : 'gif')} style={{ color: '#a55eea', cursor: 'pointer', padding: '5px 8px', background: 'rgba(165, 94, 234, 0.1)', borderRadius: '10px' }}>👾 GIF</span>
              <span onClick={() => setCommentMenu(commentMenu === 'emoji' ? null : 'emoji')} style={{ color: '#ffa502', cursor: 'pointer', padding: '5px 8px', background: 'rgba(255, 165, 2, 0.1)', borderRadius: '10px' }}>🙂 Emoji</span>

              {commentMenu && (
                  <div style={{ position: 'absolute', bottom: '100%', left: 0, marginBottom: '8px', zIndex: 200, background: 'rgba(30, 30, 36, 0.98)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', padding: '10px', boxShadow: '0 10px 40px rgba(0,0,0,0.5)', width: '260px' }}>
                      {commentMenu === 'emoji' && (
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '5px', maxHeight: '160px', overflowY: 'auto' }}>
                              {COMMENT_EMOJIS.map(e => <div key={e} onClick={() => addCommentEmoji(e)} style={{ fontSize: '20px', cursor: 'pointer', textAlign: 'center', padding: '4px' }}>{e}</div>)}
                          </div>
                      )}
                      {commentMenu === 'gif' && (
                          <div style={{ display: 'flex', flexDirection: 'column', height: '220px' }}>
                              <div style={{ display: 'flex', gap: '5px', marginBottom: '8px' }}>
                                  <input value={commentGifSearch} onChange={(e) => setCommentGifSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && searchCommentGifs(commentGifSearch)} placeholder="Search..." style={{ flex: 1, padding: '6px', borderRadius: '6px', border: 'none', background: 'rgba(255,255,255,0.1)', color: 'white' }} />
                                  <button onClick={() => searchCommentGifs(commentGifSearch)} style={{ background: '#a55eea', border: 'none', color: 'white', borderRadius: '6px', cursor: 'pointer' }}>🔍</button>
                              </div>
                              <div style={{ flex: 1, overflowY: 'auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
                                  {commentGifLoading && <div style={{ color: '#aaa', gridColumn: '1 / -1', textAlign: 'center' }}>Loading...</div>}
                                  {!commentGifLoading && commentGifs.map(g => (
                                      <img key={g.id} src={g.images.fixed_height.url} onClick={() => handleCommentGifSelect(g.images.original.url)} style={{ width: '100%', borderRadius: '6px', cursor: 'pointer' }} alt="gif" />
                                  ))}
                              </div>
                          </div>
                      )}
                  </div>
              )}
          </div>
        </div>
      )}

      {lightboxImage && createPortal(
          <div
              ref={lightboxRef}
              style={{
                  position: 'fixed',
                  bottom: '5vh',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 5000,
              }}
          >
              <img
                  src={lightboxImage}
                  alt="Full size"
                  onClick={() => setLightboxImage(null)}
                  style={{ display: 'block', width: 'auto', height: 'auto', maxWidth: `min(${lightboxWidth}px, 95vw)`, maxHeight: '90vh', objectFit: 'contain', margin: 0, borderRadius: '8px', boxShadow: '0 10px 30px rgba(0,0,0,0.5)', cursor: 'pointer' }}
              />
              <button onClick={() => setLightboxImage(null)} style={{ position: 'absolute', top: '-14px', right: '-14px', background: '#2f3542', color: 'white', border: '2px solid #1e1e24', borderRadius: '50%', width: '30px', height: '30px', fontSize: '16px', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>✕</button>
          </div>,
          document.body
      )}
    </div>
  );
}

export default Post;
